import type { ApiTour, ApiTourProblem, ApiTourStatus, ApiTourStop } from '#api/tourservice/model/types.ts';
import type { Stop } from '#domain/model/Stop.ts';
import type { Tour, TourProblem, TourProblemType, TourStatus } from '#domain/model/Tour.ts';

import { mapServiceOrders } from './serviceOrderAdapter.ts';
import { mapStop, mapStops } from './stopAdapter.ts';
import { mapTransportOrders } from './transportOrderAdapter.ts';

const getStops = (tour: { stops?: ApiTourStop[] }): ApiTourStop[] => tour.stops ?? [];

const getFirstStop = (tour: { stops?: ApiTourStop[] }): Stop | undefined => {
    const stops = getStops(tour);
    return stops[0] ? mapStop(stops[0]) : undefined;
};

const getLastStop = (tour: { stops?: ApiTourStop[] }): Stop | undefined => {
    const stops = getStops(tour);
    return stops.length > 0 ? mapStop(stops[stops.length - 1]) : undefined;
};

const tourProblemTypes = [
    'other',
    'call-back',
    'declined-goods',
    'problem-with-customer',
    'customer-not-available',
    'accident',
    'heavy-traffic',
    'damage',
    'running-late',
] as const satisfies readonly TourProblemType[];

const isTourProblemType = (type: string): type is TourProblemType => tourProblemTypes.includes(type as TourProblemType);

const mapTourProblem = (problem: ApiTourProblem | undefined): TourProblem | undefined => {
    if (!problem) {
        return;
    }

    return {
        id: problem.id,
        type: isTourProblemType(problem.type) ? problem.type : 'other',
    };
};

const mapStatus = (status: ApiTourStatus, hasProblems: boolean): TourStatus => {
    if (hasProblems && status !== 'finished') {
        return 'problem';
    }
    switch (status) {
        case 'planned':
            return 'planned';
        case 'in-transit':
            return 'in-transit';
        case 'handling':
            return 'in-transit';
        case 'finished':
            return 'finished';
        default:
            return 'open';
    }
};

export const mapTour = (tour: ApiTour): Tour => ({
    id: tour.id,
    note: tour.note,
    searchName: tour.name ?? '',
    driverId: tour.drivers.length > 0 ? tour.drivers[0] : undefined,
    truck: {
        id: tour.means_of_transport?.asset_id ?? '',
        licensePlate: tour.means_of_transport?.license_plate?.plate_number,
    },
    trailer: {
        id: tour.trailer?.asset_id,
        licensePlate: tour.trailer?.license_plate?.plate_number,
    },
    status: mapStatus(tour.status, (tour.problems ?? []).length > 0),
    progress: {
        totalStopAmount: tour.stops.length,
        finishedStopAmount: tour.stops.filter(s => !!s.finished_at).length,
    },
    firstStop: getFirstStop(tour),
    lastStop: getLastStop(tour),
    problem: mapTourProblem(tour.problems?.find(p => p.acknowledged_at === undefined)),
    stops: mapStops(tour.stops),
    transportOrders: mapTransportOrders(tour.transport_orders ?? []),
    serviceOrders: mapServiceOrders(tour.service_orders ?? []),
    seenByDriver: !!tour.seen_at,
});

export const mapTours = (tours: ApiTour[]): Tour[] => tours.map(mapTour);
